import { mutationNamespace, queryNamespace } from '@libs/shared/frontend/helpers/query-namespace.helper';
import { mutationOptions, queryOptions } from '@tanstack/react-query';

import { client, type components, HttpException } from '@/libs/openapi';
import { queryClient } from '@/main';
import { fromApiUserFieldAdapter } from '@/resources/user/user.field-adapter';
import { Resource } from '@/types/resource.type';

export const userQueries = queryNamespace(Resource.user, {
  getPasswordStrength: (body: components['schemas']['ValidatePasswordStrengthDto']) => queryOptions({
    queryKey: [ body.password ],
    queryFn: ({ signal }) => client
      .POST('/customer/api/tcd/users/password-strength', { body, signal })
      .then(({ data, response }) => {
        if (!response.ok) {
          throw new HttpException({
            message: response.statusText,
            response,
            isGlobal: false,
          });
        }

        return data!;
      }),
  }),
  me: queryOptions({
    queryKey: [],
    queryFn: ({ signal }) => client
      .GET('/customer/api/tcd/me', { signal })
      .then(({ data, error, response }) => {
        if (error) {
          throw new HttpException({
            message: Array.isArray(error.message) ? error.message[0] : error.message,
            response,
            isGlobal: false,
          });
        }

        return data;
      }),
    staleTime: Infinity,
    select: (data) => ({
      original: data,
      transformed: fromApiUserFieldAdapter.transform(data),
      meta: {
        initials: fromApiUserFieldAdapter.getInitials(data),
        fullName: fromApiUserFieldAdapter.getFullName(data),
      },
    }),
  }),
});

export const userMutations = mutationNamespace(Resource.user, {
  login: mutationOptions({
    mutationFn: () =>
      client
        .POST('/customer/api/tcd/me/login', {})
        .then(({ response }) => {
          if (!response.ok) {
            throw new HttpException({
              message: response.statusText,
              response,
              isGlobal: false,
            });
          }
        }),
  }),
  updateMe: mutationOptions({
    mutationFn: (body: components['schemas']['UpdateMeDto']) =>
      client
        .PATCH('/customer/api/tcd/me', { body })
        .then(({ data, error, response }) => {
          if (error) {
            throw new HttpException({
              message: Array.isArray(error.message) ? error.message[0] : error.message,
              response,
              isGlobal: false,
            });
          }

          return data;
        }),
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: userQueries.me.queryKey });

      const previousMe = queryClient.getQueryData(userQueries.me.queryKey);

      queryClient.setQueryData(userQueries.me.queryKey, (old) => (old && { ...old, ...body }));

      return { previousMe };
    },
    onError: (_error, _variables, context) => {
      context?.previousMe && queryClient.setQueryData(userQueries.me.queryKey, context.previousMe);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: userQueries.me.queryKey });
    },
  }),
});
